// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

const MASK_GROUP = '••••';
const SP = ' ';

/** Normalizes an IBAN string (removes spaces) and returns empty string if invalid. */
export function normalizeIban(iban: string | null | undefined): string {
  if (iban === null || typeof iban !== 'string') return '';
  return iban.replace(/\s/g, '');
}

/** Formats a normalized IBAN with a space every four characters for display. */
export function formatIbanWithGroups(iban: string | null | undefined): string {
  const raw = normalizeIban(iban);
  if (!raw) return '-';

  return raw.replace(/(.{4})/g, '$1 ').trim();
}

/** Builds a masked segment as "•••• •••• •••• ..." with the given number of groups. */
function maskedGroups(count: number): string {
  return Array.from({ length: count }, () => MASK_GROUP).join(SP);
}

/**
 * Mask styles for IBAN display.
 *
 * - `first-and-last`: first 4 + 4 masked groups + last 4 (e.g. ES19 •••• •••• ••••
 *   •••• 1065)
 * - `first4-last4`: first 4 + one mask + last 4 (e.g. ES82 •••• 5428)
 * - `last-only`: only last 4 visible (e.g. •••• 1065)
 * - `first-and-last-four`: alias for first-and-last
 */
export type IbanMaskFormat =
  | 'first-and-last'
  | 'first4-last4'
  | 'last-only'
  | 'first-and-last-four';

/**
 * Masks an IBAN for display.
 *
 * @example
 *
 * ```ts
 * maskIban('ES1987692655672752471065', 'first-and-last');
 * // 'ES19 •••• •••• •••• •••• 1065'
 *
 * maskIban('ES1987692655672752471065', 'last-only');
 * // '•••• 1065'
 * ```
 *
 * @param iban - Raw IBAN (spaces are stripped).
 * @param format - Mask style (`first-and-last`, `first4-last4`, `last-only`).
 * @returns Masked string or `'-'` when the IBAN is empty.
 */
export function maskIban(
  iban: string | null | undefined,
  format: IbanMaskFormat = 'first-and-last',
): string {
  const raw = normalizeIban(iban);
  if (raw.length < 4) return raw ? raw : '-';

  const last4 = raw.slice(-4);
  if (format === 'last-only') {
    return `${MASK_GROUP}${SP}${last4}`;
  }

  if (raw.length < 8) return raw ? raw : '-';
  const first4 = raw.slice(0, 4);
  if (format === 'first4-last4') {
    return `${first4}${SP}${MASK_GROUP}${SP}${last4}`;
  }
  const middleMask = maskedGroups(4);
  return `${first4}${SP}${middleMask}${SP}${last4}`.trim();
}
